/**
 * Template Hooks
 *
 * React Query hooks for template marketplace operations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { templateService } from '../services/template';
import type { PublishTemplateData } from '../services/template';
import {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateFilters,
} from '../types/template';

// Query keys for templates
export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (filters: TemplateFilters | Record<string, never>) => [...templateKeys.lists(), filters] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: string) => [...templateKeys.details(), id] as const,
  featured: () => [...templateKeys.all, 'featured'] as const,
  popular: (limit?: number) => [...templateKeys.all, 'popular', limit] as const,
  categoryCounts: () => [...templateKeys.all, 'categoryCounts'] as const,
};

/**
 * Hook to list templates with pagination and filters
 *
 * @param filters - Query parameters for filtering
 * @returns Query result with paginated templates
 */
export function useTemplates(filters?: TemplateFilters) {
  return useQuery({
    queryKey: templateKeys.list(filters || {}),
    queryFn: () => templateService.list(filters),
  });
}

/**
 * Hook to fetch a single template
 *
 * @param templateId - The template ID to fetch
 * @returns Query result with template data
 */
export function useTemplate(templateId: string | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(templateId!),
    queryFn: () => templateService.get(templateId!),
    enabled: !!templateId,
  });
}

/**
 * Hook to fetch featured templates
 *
 * @returns Query result with featured templates
 */
export function useFeaturedTemplates() {
  return useQuery({
    queryKey: templateKeys.featured(),
    queryFn: () => templateService.getFeatured(),
  });
}

/**
 * Hook to fetch popular templates
 *
 * @param limit - Number of templates to fetch
 * @returns Query result with popular templates
 */
export function usePopularTemplates(limit: number = 10) {
  return useQuery({
    queryKey: templateKeys.popular(limit),
    queryFn: () => templateService.getPopular(limit),
  });
}

/**
 * Hook to fetch category counts
 *
 * @returns Query result with category counts
 */
export function useCategoryCounts() {
  return useQuery({
    queryKey: templateKeys.categoryCounts(),
    queryFn: () => templateService.getCategoryCounts(),
  });
}

/**
 * Hook to create a template
 *
 * @returns Mutation object for creating a template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) => templateService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: templateKeys.categoryCounts() });
      toast.success('Template created successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to create template';
      toast.error(message);
    },
  });
}

/**
 * Hook to publish an agent as a marketplace template (submitted for admin review).
 *
 * @returns Mutation object for publishing a template
 */
export function usePublishTemplate() {
  return useMutation({
    mutationFn: (data: PublishTemplateData) => templateService.publishAsTemplate(data),
    onSuccess: () => {
      toast.success("Submitted for review — it'll appear in the marketplace once approved.");
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to submit template';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a template
 *
 * @returns Mutation object for updating a template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateRequest }) =>
      templateService.update(id, data),
    onSuccess: (template) => {
      queryClient.setQueryData(templateKeys.detail(template.id), template);
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast.success('Template updated successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to update template';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a template
 *
 * @returns Mutation object for deleting a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => templateService.delete(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: templateKeys.categoryCounts() });
      toast.success('Template deleted successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to delete template';
      toast.error(message);
    },
  });
}

/**
 * Hook to use a template (create agent from template)
 *
 * @returns Mutation object for using a template
 */
export function useUseTemplate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name?: string }) =>
      templateService.useTemplate(templateId, name),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast.success(`Agent "${response.name}" created from template!`, {
        icon: '🎉',
        duration: 4000,
      });
      navigate(`/agents/${response.id}`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to use template';
      toast.error(message);
    },
  });
}

/**
 * Hook to fork a template
 */
export function useForkTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name?: string }) =>
      templateService.fork(templateId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast.success('Template forked successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to fork template';
      toast.error(message);
    },
  });
}

/**
 * Hook to rate a template
 *
 * @returns Mutation object for rating a template
 */
export function useRateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, rating }: { templateId: string; rating: number }) =>
      templateService.rate(templateId, rating),
    onSuccess: (template) => {
      queryClient.setQueryData(templateKeys.detail(template.id), template);
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast.success('Thanks for your rating!', { icon: '⭐' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to rate template';
      toast.error(message);
    },
  });
}
